import { PageInfoType } from '@op/types/common';
import React, { useMemo, useRef, useState } from 'react';
import { Bubble, Checkbox, Modal } from 'tea-component';
import { useIntl } from 'umi';
import './index.scss';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';

const colorSortMap: any = {
  '#5086f3': 1,
  '#69d19c': 2,
  '#6e7794': 3,
  '#f5d242': 4,
  '#7f6ff1': 5,
  '#5ec8f1': 6,
  '#ad53d4': 7,
  '#f19e55': 8,
  '#488e89': 9,
  '#f17c86': 10,
  '#a6c9ff': 11,
  '#c0ebd2': 12,
  '#b8bed1': 13,
  '#fff196': 14,
  '#d2c8fd': 15,
  '#b5efff': 16,
  '#dfb2ed': 17,
  '#ffd8ab': 18,
  '#a9c7c3': 19,
  '#ffcccc': 20,
};

export type TableColorType = {
  [key: string]: {
    color?: string;
    name?: string;
    sort?: number;
    icon: string;
    channel: string;
  };
};

const colors: any = Object.keys(colorSortMap);
export default function useTableSelect({
  tableKey,
  maxCount = 10,
  allSelectCount = 6,
  defaultSelect = 6,
}: {
  tableKey: string;
  maxCount?: number;
  allSelectCount?: number;
  defaultSelect?: number;
}) {
  // 获取className
  const getClassName = (rowKey: string | number) => {
    const key = selectedKeysMap[rowKey]?.color || '';
    return `color-${key?.substring(1)}`;
  };
  const allSelectInfo = useRef<Array<any>>([]);
  function clearAllSelectInfo() {
    allSelectInfo.current = [];
  }

  const [selectedKeysMap, setSelectedKeysMap] = useState<TableColorType>({});
  const { formatMessage } = useIntl();
  const saveDefaultData = (list: Array<any>, needUpdate = false) => {
    if (allSelectInfo.current.length === 0 || needUpdate) {
      colorArr.current = [...colors];
      const selectKeys: any = {};
      const newSelectList: Array<any> = [];
      list?.some((item, index) => {
        if (!item) return true;
        const key = item[tableKey];
        if (index < allSelectCount) {
          newSelectList.push({
            name: item.name || key,
            sort: index,
            [tableKey]: key,
          });
        }
        if (index < defaultSelect) {
          const color = colorArr.current.shift();
          selectKeys[key] = {
            color,
            name: item.name || key,
            sort: index,
            icon: item.avatar,
            channel: item.channel,
            [tableKey]: key,
          };
        }

        // 默认选中allSelectCount个数据
        return index >= allSelectCount;
      });
      sortColorArr();
      allSelectInfo.current = newSelectList;
      setSelectedKeysMap(selectKeys);
      setSelectKeys(Object.keys(selectKeys));
    }
  };

  const colorArr = useRef([...colors]);
  const [selectColors] = useMemo(() => {
    const colors: Array<string> = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const key in selectedKeysMap) {
      const element = selectedKeysMap[key] || {};
      if (element?.color) {
        colors.push(element.color);
      }
    }
    return [colors];
  }, [selectedKeysMap]);
  const [selectKeys, setSelectKeys] = useState<Array<string>>([]);
  const sortColorArr = () => {
    colorArr.current.sort((a, b) => colorSortMap[a] - colorSortMap[b]);
  };

  const [selectAll, halfSelect] = useMemo(() => {
    const all = selectKeys.length >= allSelectCount;
    const half = !all && selectKeys.length > 0;
    return [all, half];
  }, [selectKeys]);

  // 单选处理
  const tableSingleSelectChange = (
    selectedObj: any,
    checked: boolean,
    recordKey: string,
  ) => {
    if (checked) {
      // 取一个颜色
      const color = colorArr.current.shift();
      if (Object.keys(selectedKeysMap).length < maxCount) {
        return { ...selectedKeysMap, [recordKey]: { color, ...selectedObj } };
      }
      // 报错, 不用变化，返回原对象就好
      Modal.error({
        message: formatMessage({ id: 'Sorry' }),
        description: formatMessage(
          {
            id: 'Please select {count} items at most for the best visualization experience.',
          },
          { count: maxCount },
        ),
      });
      return selectedKeysMap;
    }
    // 回收颜色
    const { color } = selectedKeysMap[recordKey] || {};
    if (color) {
      colorArr.current.unshift(color);
    }
    const newObj: any = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const key in selectedKeysMap) {
      if (key !== recordKey) {
        newObj[key] = selectedKeysMap[key];
      }
    }
    return newObj;
  };
  const tableSelectChange = (
    recordKey: string,
    selectedObj: any,
    checked: boolean,
    selectAll = false,
  ) => {
    let localSelectKeysMap: any = {};
    if (selectAll) {
      if (checked) {
        // 全选逻辑
        allSelectInfo.current.forEach((item, index) => {
          if (index < allSelectCount) {
            const key = item[tableKey];
            const color = selectedKeysMap[key]?.color;
            localSelectKeysMap[key] = {
              color: color ? color : colorArr.current.shift(),
              name: item.name || recordKey,
              sort: item.sort,
              icon: item.avatar,
              channel: item.channel,
              [tableKey]: key,
            };
          }
        });
      } else {
        // 处理全不选逻辑 归还所有颜色
        colorArr.current.unshift(...selectColors);
      }
    } else {
      // 单选处理
      localSelectKeysMap = tableSingleSelectChange(
        selectedObj,
        checked,
        recordKey,
      );
    }
    sortColorArr();
    setSelectKeys(Object.keys(localSelectKeysMap));
    setSelectedKeysMap(localSelectKeysMap);
    if (selectAll) {
      buriedBtnPoint('SelectedItem', `top ${allSelectCount}`);
    } else if (checked) {
      buriedBtnPoint('SelectedItem', selectedObj[tableKey]);
    }
  };

  const commonIndexColumns = ({
    headerInfo,
    dealwithItemElement,
    pageInfo,
    width = 92,
    tips,
    hasAllSelect = true,
  }: {
    headerInfo: any;
    dealwithItemElement?: (item: {
      defaultIndex: string | number;
      record: any;
      key: string;
      index: number;
    }) => any;
    pageInfo: PageInfoType;
    width?: number | string;
    tips?: string;
    hasAllSelect?: boolean;
  }) => {
    return {
      key: 'Index',
      header: hasAllSelect ? (
        <Bubble
          dark
          closeOnScroll
          content={tips ? formatMessage({ id: tips }) : null}
          placement="top"
        >
          <Checkbox
            onChange={(value) => tableSelectChange('', {}, value, true)}
            indeterminate={halfSelect}
            name="__selectable_all__"
            value={selectAll}
          >
            {headerInfo}
          </Checkbox>
        </Bubble>
      ) : (
        headerInfo
      ),
      width,
      fixed: 'left',
      render: (item: any, key: string, index: number) => {
        const indexText = pageInfo
          ? (pageInfo.page - 1) * pageInfo.page_size + index + 1
          : '';
        const { name } = item;
        const onlyId = item[tableKey];
        return (
          <span className={`select-table-checkbox ${getClassName(onlyId)}`}>
            <Checkbox
              onChange={(value) =>
                tableSelectChange(
                  onlyId,
                  {
                    name,
                    sort: indexText,
                    icon: item.avatar,
                    channel: item.channel,
                    [tableKey]: onlyId,
                  },
                  value,
                )
              }
              name={onlyId}
              value={selectKeys.includes(onlyId)}
            >
              {dealwithItemElement?.({
                defaultIndex: indexText,
                record: item,
                key,
                index,
              })}
            </Checkbox>
          </span>
        );
      },
    };
  };

  return {
    saveDefaultData,
    selectKeys,
    allSelectInfo,
    selectedKeysMap,
    commonIndexColumns,
    clearAllSelectInfo,
  };
}
